import PitchData from '../../enums/PitchData.js';
import HitData from '../../enums/HitData.js';

class MlbSensorDataConverter {
    constructor() {
        this.inchToM = 2.54 / 100;

        this.pitchTemplate = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/data/result-sensor-pitch-v2.json
            // unfortuntaley the live game data feed sometimes doesn't contain too much data...
            [PitchData.StrikeZoneTop.key]: null,
            [PitchData.StrikeZoneBottom.key]: null,
            // however, in regular season we can get some more info here
            [PitchData.Extension.key]: null,
            [PitchData.Speed.key]: null,
            [PitchData.ApproachSpeed.key]: null,
            [PitchData.FlightTime.key]: null,
            [PitchData.StrikeZonePosition.key]: null,
            // in breaks we can find more...
            [PitchData.Spin.key]: null,
            [PitchData.SpinDirection.key]: null,
            //[PitchData.BreakH.key]: null,
            //[PitchData.BreakVInd.key]: null,
            // in coordinates node, we can find even MORE
            [PitchData.AX.key]: null,
            [PitchData.AY.key]: null,
            [PitchData.AZ.key]: null,
            [PitchData.PX.key]: null,
            [PitchData.PZ.key]: null,
            [PitchData.PFXX.key]: null,
            [PitchData.PFXZ.key]: null,
            [PitchData.VX0.key]: null,
            [PitchData.VY0.key]: null,
            [PitchData.VZ0.key]: null,
            [PitchData.X0.key]: null,
            [PitchData.Y0.key]: null,
            [PitchData.Z0.key]: null,
            [PitchData.Guid.key]: null,
            // TODO, coordinates node contains also X and Y, but I have no idea what it means
        };

        this.hitTemplate = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/data/result-sensor-hit-v2.json
            [HitData.LaunchV.key]: null,
            [HitData.ExitSpeed.key]: null,
            [HitData.CarryDistance.key]: null,
            [HitData.LandingPoint.key]: { x: null, y: null, z: null },
            [HitData.Guid.key]: null,
        };

        // unfortunately hitData.coordinates looks like image coordiantes ? (pixels)
        // TODO - check if we can make it more precise, I've based on the following example:
        // totalDistance: 246yd, coordX: 140.1, coordY: 79.21 (liveData.plays.allPlays[3].playEvents[6].hitData.coordinates from https://www.mlb.com/gameday/braves-vs-pirates/2021/07/07/633330#game_state=final,game_tab=play-by-play,game=633330)
        // so totalDistance will be 225m,
        // distance in pixels is (140.1^2 + 79.21^2)^0.5 = 160.94 px
        // 1px = 1.398m (225 m/160,94 px)
        this.pxToMScale = 1.398;
    }

    /**
     * Returns object with pitch and hit property from a single play event
     * @param {Object} playEvent - item from liveData.plays.allPlays[].playEvents, Note, only playEvents of type "ptich" are supported
     */
    convert(playEvent) {
        const sensorData = {};

        if (playEvent.pitchData) {
            sensorData.pitch = this.convertPitchData(
                playEvent.pitchData, playEvent.playId
            );
        }

        if (playEvent.hitData) {
            sensorData.hit = this.convertHitData(
                playEvent.hitData, playEvent.playId
            );
        }

        return sensorData;
    }

    convertPitchData(pitchData, guid) {
        const pitch = Object.assign({}, this.pitchTemplate);

        // this documents can be useful in terms of units and meaning of data
        // @see https://fastballs.wordpress.com/category/pitchfx-glossary/
        // @see https://physics.csuchico.edu/baseball/resources/POBActivities/POB/PitchFX.pdf

        if (guid) {
            pitch[PitchData.Guid.key] = `{${guid}}`;
        }

        // start_speed: the pitch speed, in miles per hour and in three dimensions, measured at the initial point, y0. Of the two speeds, this one is closer to the speed measured by a radar gun and what we are familiar with for a pitcher’s “velocity” .
        if (pitchData.startSpeed) {
            pitch[PitchData.Speed.key] = this.milesToMeters(pitchData.startSpeed) / 3600;
        }

        // end_speed: the pitch speed measured as it crossed the front of home plate.
        if (pitchData.endSpeed) {
            pitch[PitchData.ApproachSpeed.key] = this.milesToMeters(pitchData.endSpeed) / 3600;
        }

        // extension seems to be in ft
        if (pitchData.extension) {
            pitch[PitchData.Extension.key] = this.footToMeters(pitchData.extension);
        }

        if (pitchData.plateTime) {
            pitch[PitchData.FlightTime.key] = pitchData.plateTime;
        }

        // sz_bot: the distance in feet from the ground to the bottom of the current batter’s rulebook strike zone. The PITCHf/x operator sets a line at the hollow of the knee for the bottom of the zone.
        if (pitchData.strikeZoneBottom) {
            pitch[PitchData.StrikeZoneBottom.key] = this.footToMeters(pitchData.strikeZoneBottom);
        }

        // sz_top: the distance in feet from the ground to the top of the current batter’s rulebook strike zone as measured from the video by the PITCHf/x operator. The operator sets a line at the batter’s belt as he settles into the hitting position, and the PITCHf/x software adds four inches up for the top of the zone.
        if (pitchData.strikeZoneTop) {
            pitch[PitchData.StrikeZoneTop.key] = this.footToMeters(pitchData.strikeZoneTop);
        }

        // now let's look into breaks property
        if (pitchData.breaks) {
            if (!Number.isNaN(pitchData.breaks.spinDirection) && typeof(pitchData.breaks.spinDirection) !== 'undefined') {
                pitch[PitchData.SpinDirection.key] = pitchData.breaks.spinDirection;
            }
            if (pitchData.breaks.spinRate) {
                pitch[PitchData.Spin.key] = pitchData.breaks.spinRate;
            }
            // TODO - can we use breakAngle, breakLength and breakY somehow ? @see https://fastballs.files.wordpress.com/2008/06/pfx_break_from_walsh.jpg https://tht.fangraphs.com/in-search-of-the-sinker/
        }

        // and coordinates property
        if (pitchData.coordinates) {
            // ax, ay, az: the acceleration of the pitch, in feet per second per second, in three dimensions, measured at the initial point.
            if (!Number.isNaN(pitchData.coordinates.aX) && typeof(pitchData.coordinates.aX) !== 'undefined') {
                pitch[PitchData.AX.key] = this.footToMeters(pitchData.coordinates.aX);
            }
            if (!Number.isNaN(pitchData.coordinates.aY) && typeof(pitchData.coordinates.aY) !== 'undefined') {
                pitch[PitchData.AY.key] = this.footToMeters(pitchData.coordinates.aY);
            }
            if (!Number.isNaN(pitchData.coordinates.aZ) && typeof(pitchData.coordinates.aZ) !== 'undefined') {
                pitch[PitchData.AZ.key] = this.footToMeters(pitchData.coordinates.aZ);
            }

            // px: the left/right distance, in feet, of the pitch from the middle of the plate as it crossed home plate. The PITCHf/x coordinate system is oriented to the catcher’s/umpire’s perspective, with distances to the right being positive and to the left being negative.
            if (!Number.isNaN(pitchData.coordinates.pX) && typeof(pitchData.coordinates.pX) !== 'undefined') {
                pitch[PitchData.PX.key] = this.footToMeters(pitchData.coordinates.pX);
            }
            // pz: the height of the pitch in feet as it crossed the front of home plate.
            if (!Number.isNaN(pitchData.coordinates.pZ) && typeof(pitchData.coordinates.pZ) !== 'undefined') {
                pitch[PitchData.PZ.key] = this.footToMeters(pitchData.coordinates.pZ);
            }

            if (pitch[PitchData.PX.key] && pitch[PitchData.PZ.key]) {
                // TODO - verify it
                // we need X; Y; Z as a string, for example -0.2020934; 0.8938947; -0.5715842; 0; 0
                pitch[PitchData.StrikeZoneFront.key] = `${pitch[PitchData.PX.key]}; 0; ${pitch[PitchData.PZ.key]}`;
            }

            // x0: the left/right distance, in feet, of the pitch, measured at the initial point.
            if (!Number.isNaN(pitchData.coordinates.x0) && typeof(pitchData.coordinates.x0) !== 'undefined') {
                pitch[PitchData.X0.key] = this.footToMeters(pitchData.coordinates.x0);
            }
            // y0: the distance in feet from home plate where the PITCHf/x system is set to measure the initial parameters. This parameter has been variously set at 40, 50, or 55 feet (and in a few instances 45 feet) from the plate at different times throughout the 2007 season as Sportvision experiments with optimal settings for the PITCHf/x measurements. Sportvision settled on 50 feet in the second half of 2007, and this value of y0=50 feet has been used since. Changes in this parameter impact the values of all other parameters measured at the release point, such as start_speed.
            if (!Number.isNaN(pitchData.coordinates.y0) && typeof(pitchData.coordinates.y0) !== 'undefined') {
                pitch[PitchData.Y0.key] = this.footToMeters(pitchData.coordinates.y0);
            }
            // z0: the height, in feet, of the pitch, measured at the initial point.
            if (!Number.isNaN(pitchData.coordinates.z0) && typeof(pitchData.coordinates.z0) !== 'undefined') {
                pitch[PitchData.Z0.key] = this.footToMeters(pitchData.coordinates.z0);
            }

            // pfx_x: the horizontal movement, in inches, of the pitch between the release point and home plate, as compared to a theoretical pitch thrown at the same speed with no spin-induced movement. This parameter is measured at y=40 feet regardless of the y0 value.
            if (!Number.isNaN(pitchData.coordinates.pfxX) && typeof(pitchData.coordinates.pfxX) !== 'undefined') {
                pitch[PitchData.PFXX.key] = this.inchesToMeters(pitchData.coordinates.pfxX);
            }
            // pfx_z: the vertical movement, in inches, of the pitch between the release point and home plate, as compared to a theoretical pitch thrown at the same speed with no spin-induced movement. This parameter is measured at y=40 feet regardless of the y0 value.
            if (!Number.isNaN(pitchData.coordinates.pfxZ) && typeof(pitchData.coordinates.pfxZ) !== 'undefined') {
                pitch[PitchData.PFXZ.key] = this.inchesToMeters(pitchData.coordinates.pfxZ);
            }

            // vx0, vy0, vz0: the velocity of the pitch, in feet per second, in three dimensions, measured at the initial point.
            if (!Number.isNaN(pitchData.coordinates.vX0) && typeof(pitchData.coordinates.vX0) !== 'undefined') {
                pitch[PitchData.VX0.key] = this.footToMeters(pitchData.coordinates.vX0);
            }
            if (!Number.isNaN(pitchData.coordinates.vY0) && typeof(pitchData.coordinates.vY0) !== 'undefined') {
                pitch[PitchData.VY0.key] = this.footToMeters(pitchData.coordinates.vY0);
            }
            if (!Number.isNaN(pitchData.coordinates.vZ0) && typeof(pitchData.coordinates.vZ0) !== 'undefined') {
                pitch[PitchData.VZ0.key] = this.footToMeters(pitchData.coordinates.vZ0);
            }
        }

        return pitch;
    }

    convertHitData(hitData, guid) {
        const hit = Object.assign({}, this.hitTemplate);
        // TODO - createDateUtc used by the VideoClipper ?

        if (guid) {
            hit[HitData.Guid.key] = `{${guid}}`;
        }

        // @see https://pkg.go.dev/github.com/mikydna/gl/mlb/statsapi#FeedPlayEvent
        if (!Number.isNaN(hitData.launchAngle) && typeof(hitData.launchAngle) !== 'undefined') {
            hit[HitData.LaunchV.key] = hitData.launchAngle;
        }
        if (!Number.isNaN(hitData.launchSpeed) && typeof(hitData.launchSpeed) !== 'undefined') {
            hit[HitData.ExitSpeed.key] = this.milesToMeters(hitData.launchSpeed) / 3600;
        }
        if (!Number.isNaN(hitData.totalDistance) && typeof(hitData.totalDistance) !== 'undefined') {
            // TODO - mlb says total, we have carry
            hit[HitData.CarryDistance.key] = this.footToMeters(hitData.totalDistance);
        }

        if (hitData.coordinates) {
            // TODO - this is FAKE, maybe use pitch location on the strike zone ?
            hit[HitData.HitPoint.key] = '0; 0; 0';
            hit[HitData.LandingPoint.key] = `${(hitData.coordinates.coordX * this.pxToMScale)}; ${(hitData.coordinates.coordY * this.pxToMScale)}; 0`;
        }

        return hit;
    }

    // TODO - inject units-converter?
    milesToMeters(miles) {
        return this.footToMeters(miles * 5280);
    }

    footToMeters(foot) {
        return this.inchesToMeters(foot * 12);
    }

    inchesToMeters(inches) {
        return inches * this.inchToM;
    }
}

export default MlbSensorDataConverter;